import { formatFloat } from '../../../utility/utility_functions';
import { PropertyDetailsProps, ApproachType } from '../../../utility/types';
export class CalculatedApproach {
  static SYSTEM_CALCULATION_KEY = 'System Calculation';
  static DO_NOT_USE_KEY = 'Do Not Use';

  priorityLeins: number;
  proratedTaxes: number;
  max_bid: number;
  max_bid_per_sqft: number;
  estimated_profit: number;
  roi: number;
  approach: ApproachType;
  // Fixed costs actually used by this approach (0 when the approach or the user's settings exclude them)
  fixedCosts: number;
  fixedCostsApplied: boolean;
  estimatedValue: number;
  totalCosts: number;
  userProvidedValuation?: number;
  userProvidedFixedCosts?: number;

  closingCosts: number;

  // annualTaxRate is a percent (2.2 means 2.2%), taken from the user's Taxes settings.
  // applyFixedCosts comes from the user's Fixed Costs settings ("Max bid, ROI and net sheet").
  constructor(approach: any, propertyDetails: PropertyDetailsProps, fixedCosts: number, estimatedValue: number, annualTaxRate = 0, applyFixedCosts = true) {
    // Be careful with order, these all depend on approach
    this.approach = approach;

    this.estimatedValue = estimatedValue;
    this.fixedCostsApplied = applyFixedCosts && approach?.fixed_cost_calculation !== CalculatedApproach.DO_NOT_USE_KEY;
    this.fixedCosts = this.fixedCostsApplied ? (fixedCosts || 0) : 0;
    this.priorityLeins = this.calculate_priority_leins(propertyDetails);
    this.proratedTaxes = this.calculate_prorated_taxes(propertyDetails, annualTaxRate);
    // Paid out of the sale proceeds at closing, so it is shown but never part of totalCosts / cash all-in
    this.closingCosts = this.calculate_closing_costs(this.estimatedValue);
    this.totalCosts = this.calculate_total_costs(this.fixedCosts, this.priorityLeins, this.proratedTaxes);

    this.max_bid = this.calculate_max_bid(this.estimatedValue, this.totalCosts);
    this.max_bid_per_sqft = this.calculate_max_bid_per_sqft(propertyDetails, this.max_bid);
    this.estimated_profit = this.calculate_estimated_profit(this.estimatedValue, this.max_bid, this.totalCosts);
    this.roi = this.calculate_roi(this.estimated_profit, this.max_bid, this.totalCosts);
  }

  formatDollarString(dollarValue: number, precision = 2) {
    return `\$${formatFloat(dollarValue, precision)}`;
  };

  get max_bid_string(): string {
    return this.formatDollarString(this.max_bid);
  }

  get max_bid_per_sqft_string(): string {
    return this.formatDollarString(this.max_bid_per_sqft);
  }

  get estimated_profit_string(): string {
    return this.formatDollarString(this.estimated_profit);
  }

  get roi_string(): string {
    return `${this.roi.toFixed(1)}%`;
  }

  calculate_total_costs(fixedCosts: number, priorityLeins: number, proratedTaxes: number): number {
    return fixedCosts + priorityLeins + proratedTaxes;
  }

  calculate_priority_leins(propertyDetails: PropertyDetailsProps): number {
    if (this.approach.priority_liens_calculation === CalculatedApproach.SYSTEM_CALCULATION_KEY) {
      // TODO: Need to figure out how to get priority liens data
      return 0;
    }
    return 0;
  }

  // Tax Value (assessed value) * annual tax rate / 12 * the approach's expected hold months.
  // Returns 0 when there is no Tax Value to work from.
  calculate_prorated_taxes(propertyDetails: PropertyDetailsProps, annualTaxRate: number): number {
    const taxValue = propertyDetails?.assessed_value;
    const holdMonths = this.approach?.hold_months;
    if (!taxValue || !annualTaxRate || !holdMonths) {
      return 0;
    }
    return ((taxValue * (annualTaxRate / 100)) / 12) * holdMonths;
  }

  calculate_closing_costs(estimatedValue: number): number {
    return estimatedValue * (this.approach?.sale_cost_percent ?? 0);
  }

  calculate_max_bid(estimatedValue: number, totalCosts: number): number {
    const avm_minus_sales_cost = estimatedValue * (1 - this.approach.sale_cost_percent);
    const avm_purchase_percent_of_value = estimatedValue * (1 - this.approach.purchase_percent_of_value);
    return avm_minus_sales_cost - avm_purchase_percent_of_value - totalCosts;
  }

  calculate_max_bid_per_sqft(propertyDetails: PropertyDetailsProps, max_bid: number): number {
    if (!propertyDetails?.sqft) {
      return 0;
    }
    return max_bid / propertyDetails.sqft;
  }

  calculate_estimated_profit(estimatedValue: number, maxBid: number, totalCosts: number): number {
    const avm_minus_sales_cost = estimatedValue * (1 - this.approach.sale_cost_percent);
    return avm_minus_sales_cost - maxBid -totalCosts;
  }

  calculate_roi(estimatedProfit: number, maxBid: number, totalCosts: number): number {
    const denominator = maxBid + totalCosts;
    if (denominator == 0) return 0;
    return (estimatedProfit / denominator) * 100;
  }
}
